import AddBox from '@mui/icons-material/AddBox';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import BadgeIcon from '@mui/icons-material/Badge';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import NumeroProcessoUtil from '@/util/NumeroProcessoUtil';
import AutoComplete from './AutoComplete';
import ReactComponent from './ReactComponent';
import IncluirSustentacaoOral from './sessao/IncluirSustentacaoOral';
dayjs.extend(customParseFormat);

/** @type {Array.<Object>} */
const sexos = [
    {
        valor: { sexo: 'F', texto: 'Feminino' },
        texto: 'Feminino'
    },
    {
        valor: { sexo: 'M', texto: 'Masculino' },
        texto: 'Masculino'
    }
];

const UFs = [
    'AC',
    'AL',
    'AP',
    'AM',
    'BA',
    'CE',
    'DF',
    'ES',
    'GO',
    'MA',
    'MT',
    'MS',
    'MG',
    'PA',
    'PB',
    'PR',
    'PE',
    'PI',
    'RJ',
    'RN',
    'RS',
    'RO',
    'RR',
    'SC',
    'SP',
    'SE',
    'TO'
];

const TAMANHOS_MAXIMOS_CAMPOS = {
    numeroOab: 6,
    ufOab: 2,
    letraOab: 1,
    nomeAdvogado: 250,
    dataSolicitacaoSustentacao: 19
};

const ESPACAMENTO_ITEM_FORMULARIO = '20px';

class ModalEdicaoAdvogadoProcesso extends ReactComponent {
    aberto = false;
    parteAtual;
    sexoAtual;
    advogadoInput;

    constructor(props) {
        super(props);

        makeObservable(this, {
            aberto: observable,
            parteAtual: observable,
            sexoAtual: observable,
            advogadoInput: observable,
            registro: computed,
            advogado: computed,
            init: action,
            abrir: action,
            fechar: action,
            handleClickGravarSustentacaoOral: action,
            updateSexoAtual: action,
            changeInput: action,
            onBlurNomeAdvogado: action,
            isDadosNovosIguaisAosAntigos: computed,
            dicaBotaoGravar: computed,
            msgDadosObrigatorios: computed,
            handleChangeDataSolicitacao: action,
            dataSolicitacaoSustentacao: computed
        });
    }

    get registro() {
        return this.observableProps.registro;
    }

    get advogado() {
        return this.observableProps.advogado;
    }

    componentDidMount() {
        this.init();
    }

    init() {
        this.parteAtual = toJS(this.observableProps.parteAtual);

        this.sexoAtual = this.observableProps.advogado?.genero
            ? sexos.find((sexo) => sexo.valor.sexo === this.observableProps.advogado.genero)?.texto
            : null;

        this.advogadoInput = this.observableProps.advogado
            ? toJS(this.observableProps.advogado)
            : {
                  nome: '',
                  regOAB: {
                      numero: '',
                      letra: '',
                      siglaUf: ''
                  },
                  genero: '',
                  dataSolicitacaoSustentacao: dayjs().format('DD-MM-YYYY-HH-mm-ss')
              };
    }

    abrir = () => {
        this.aberto = true;
    };

    fechar = () => {
        this.aberto = false;
    };

    handleClickGravarSustentacaoOral = () => {
        console.log(this.advogadoInput);
        if (this.advogado) {
            this.alterarSustentacaoOral();
        } else {
            this.incluirSustentacaoOral();
        }
    };

    incluirSustentacaoOral = () => {
        if (this.isSustentacaoOralCadastrada()) {
            this.mensagemStore.abrirMensagemAlerta('O advogado já está cadastrado.');
        } else {
            this.observableProps.callback && this.observableProps.callback(this.advogadoInput);
            this.fechar();
        }
    };

    verificarUF(advogado) {
        const ufOabAdvogado = advogado?.regOAB?.siglaUf;
        return ufOabAdvogado && UFs.includes(ufOabAdvogado);
    }

    alterarSustentacaoOral = async () => {
        // Correção de dados do advogado: atualiza só a identidade (nome/OAB/gênero), sem tocar
        // em modo/presença/estado nem revalidar permissão de sustentação/destaque.
        const response = await this.advogadoSustentacaoOralService.corrigirDadosAdvogado(
            this.registro.sessaoId,
            this.registro.processo.numeroCompleto,
            this.advogado,
            this.parteAtual,
            this.advogadoInput
        );

        runInAction(() => {
            if (response === true) {
                this.fechar();
                this.salaSessaoStore.recarregarProcesso(this.registro);
                this.advogadoSustentacaoOralService.notificarAlteracaoAdvogado(
                    this.registro.sessaoId,
                    this.registro.processo.numeroCompleto
                );
                this.mensagemStore.abrirMensagemSucesso('Dados alterados com sucesso.');
            } else {
                this.mensagemStore.abrirMensagemErro('Falha ao tentar gravar os dados.');
            }
        });
    };

    isSustentacaoOralCadastrada = () => {
        const advogado = this.advogadoInput;
        let retorno = false;

        if (this.registro?.pedidoSustentacaoOral?.pedidosDetalhados) {
            retorno =
                this.registro.pedidoSustentacaoOral.pedidosDetalhados.findIndex(
                    (pedido) =>
                        pedido.advogados.findIndex(
                            (adv) =>
                                adv?.regOAB?.numero &&
                                adv.regOAB.numero.toString() === advogado.regOAB.numero &&
                                adv.regOAB.siglaUf === advogado.regOAB.siglaUf &&
                                ((!adv.regOAB.letra && !advogado.regOAB.letra) ||
                                    adv.regOAB.letra === advogado.regOAB.letra) &&
                                pedido.codigoDaParte === this.parteAtual?.codigo
                        ) > -1
                ) > -1;
        }

        return retorno;
    };

    updateSexoAtual = (novoSexo) => {
        this.sexoAtual = novoSexo?.valor ? novoSexo.valor.texto : null;
        this.advogadoInput.genero = novoSexo?.valor ? novoSexo.valor.sexo : '';
    };

    changeInput = (input, event) => {
        switch (input) {
            case 'nome':
                this.advogadoInput.nome = event.target.value;
                break;
            case 'numOab':
                this.advogadoInput.regOAB.numero = event.target.value
                    .trim()
                    .replace(/\D/, '')
                    .slice(0, TAMANHOS_MAXIMOS_CAMPOS.numeroOab);
                break;
            case 'letraOab':
                this.advogadoInput.regOAB.letra = event.target.value
                    .trim()
                    .toUpperCase()
                    .replace(/[^A-Z]/, '');
                break;
            case 'ufOab':
                this.advogadoInput.regOAB.siglaUf = event.target.value
                    .trim()
                    .toUpperCase()
                    .replace(/[^A-Z]/, '')
                    .slice(0, TAMANHOS_MAXIMOS_CAMPOS.ufOab);
                break;
            default:
                break;
        }
    };

    onBlurNomeAdvogado = (event) => {
        this.advogadoInput.nome = this.tratarNomeAdvogado(event.target.value);
    };

    tratarNomeAdvogado(nome) {
        return nome ? nome.trim().replace(/ {2,}/g, ' ') : '';
    }

    get isDadosNovosIguaisAosAntigos() {
        if (!this.observableProps.advogado) {
            return false;
        }

        return (
            this.advogadoInput.nome === this.observableProps.advogado.nome &&
            this.advogadoInput.regOAB &&
            parseInt(this.advogadoInput.regOAB.numero) === parseInt(this.observableProps.advogado.regOAB.numero) &&
            ((!this.advogadoInput.regOAB.letra && !this.observableProps.advogado.regOAB.letra) ||
                this.advogadoInput.regOAB.letra === this.observableProps.advogado.regOAB.letra) &&
            this.advogadoInput.regOAB.siglaUf === this.observableProps.advogado.regOAB.siglaUf &&
            this.advogadoInput.genero === this.observableProps.advogado.genero &&
            this.advogadoInput.dataSolicitacaoSustentacao === this.observableProps.advogado.dataSolicitacaoSustentacao
        );
    }

    verificarItensObrigatoriosPreenchidos() {
        return (
            this.advogadoInput?.nome &&
            this.advogadoInput.genero &&
            this.advogadoInput.regOAB.siglaUf &&
            this.advogadoInput.regOAB.siglaUf.length === TAMANHOS_MAXIMOS_CAMPOS.ufOab &&
            this.verificarUF(this.advogadoInput) &&
            this.advogadoInput.regOAB.numero &&
            this.advogadoInput.dataSolicitacaoSustentacao &&
            this.advogadoInput.dataSolicitacaoSustentacao.length === TAMANHOS_MAXIMOS_CAMPOS.dataSolicitacaoSustentacao
        );
    }

    get dicaBotaoGravar() {
        if (this.verificarItensObrigatoriosPreenchidos()) {
            return this.isDadosNovosIguaisAosAntigos ? 'Dados idênticos aos anteriores!' : 'Gravar dados';
        }

        return this.msgDadosObrigatorios;
    }

    get msgDadosObrigatorios() {
        let msg = 'Informe: ';

        if (!this.advogadoInput) {
            msg += 'nome, dados da OAB e gênero do advogado, ';
        } else {
            if (!this.advogadoInput.nome) {
                msg += 'nome do advogado, ';
            }

            if (!this.advogadoInput.regOAB || !this.advogadoInput.regOAB.numero) {
                msg += 'dados da OAB do advogado, ';
            } else {
                if (
                    !this.advogadoInput.regOAB.siglaUf ||
                    this.advogadoInput.regOAB.siglaUf.length !== TAMANHOS_MAXIMOS_CAMPOS.ufOab
                ) {
                    msg += 'UF da OAB do advogado, ';
                } else {
                    if (!this.verificarUF(this.advogadoInput)) {
                        msg += 'UF correta da OAB do advogado, ';
                    }
                }
            }

            if (!this.advogadoInput.genero) {
                msg += 'gênero do advogado, ';
            }
        }

        const ultimaVirgula = msg.lastIndexOf(', ');
        return `${msg.slice(0, ultimaVirgula)}.`;
    }

    handleChangeDataSolicitacao = async (value) => {
        if (value && value.isValid()) {
            this.advogadoInput.dataSolicitacaoSustentacao = value.format('DD-MM-YYYY-HH-mm-ss');
        } else {
            this.advogadoInput.dataSolicitacaoSustentacao = '';
        }
    };

    get dataSolicitacaoSustentacao() {
        if (this.advogadoInput && this.advogadoInput.dataSolicitacaoSustentacao) {
            return dayjs(this.advogadoInput.dataSolicitacaoSustentacao, 'DD-MM-YYYY-HH-mm-ss');
        } else {
            return '';
        }
    }

    get tituloDialogo() {
        const numeroProcesso = NumeroProcessoUtil.formatarNumeroProcesso(
            this.registro.processo.numeroCompleto,
            false,
            true
        );
        const textoProcesso = ` (processo ${numeroProcesso})`;
        return `${this.advogado ? 'Correção de dados do advogado' : 'Inclusão de advogado'} ${textoProcesso}`;
    }

    render() {
        return (
            <>
                {this.advogado ? (
                    <>
                        <IncluirSustentacaoOral
                            registro={this.registro}
                            editar={true}
                            parte={this.observableProps.parteAtual}
                            advogado={this.observableProps.advogado}
                        />
                        <Tooltip
                            placement="top"
                            title={
                                <Typography className="tooltipBotao">
                                    Corrigir ortografia de nome, número de OAB ou gênero do representante da parte
                                </Typography>
                            }
                        >
                            <span>
                                <IconButton
                                    style={{ padding: '4px' }}
                                    onClick={this.abrir}
                                    size="large"
                                >
                                    <BadgeIcon style={{ fill: 'black' }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </>
                ) : (
                    <Tooltip
                        placement="top"
                        title="Cadastrar advogado"
                    >
                        <span>
                            <IconButton
                                onClick={this.abrir}
                                size="large"
                            >
                                <AddBox />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {this.aberto && (
                    <Dialog
                        open={this.aberto}
                        onClose={this.fechar}
                        maxWidth="lg"
                    >
                        <DialogTitle>{this.tituloDialogo}</DialogTitle>
                        <DialogContent sx={{ minWidth: '200px', width: '600px', maxWidth: 'auto' }}>
                            <>
                                <Box>
                                    <TextField
                                        fullWidth
                                        label="Nome"
                                        value={this.advogadoInput.nome}
                                        onInput={this.changeInput.bind(this, 'nome')}
                                        onBlur={this.onBlurNomeAdvogado}
                                        slotProps={{
                                            htmlInput: { maxLength: TAMANHOS_MAXIMOS_CAMPOS.nomeAdvogado }
                                        }}
                                    />
                                </Box>
                                <Box sx={{ mt: ESPACAMENTO_ITEM_FORMULARIO }}>
                                    <TextField
                                        label="Número da OAB"
                                        value={this.advogadoInput.regOAB.numero}
                                        onInput={this.changeInput.bind(this, 'numOab')}
                                        disabled={false}
                                        slotProps={{
                                            htmlInput: { maxLength: TAMANHOS_MAXIMOS_CAMPOS.numeroOab }
                                        }}
                                    />
                                </Box>
                                <Box sx={{ mt: ESPACAMENTO_ITEM_FORMULARIO }}>
                                    <TextField
                                        label="Letra da OAB"
                                        value={this.advogadoInput.regOAB.letra}
                                        onInput={this.changeInput.bind(this, 'letraOab')}
                                        disabled={false}
                                        slotProps={{
                                            htmlInput: { maxLength: TAMANHOS_MAXIMOS_CAMPOS.letraOab }
                                        }}
                                    />
                                </Box>
                                <Box sx={{ mt: ESPACAMENTO_ITEM_FORMULARIO }}>
                                    <TextField
                                        label="UF da OAB"
                                        value={this.advogadoInput.regOAB.siglaUf}
                                        onInput={this.changeInput.bind(this, 'ufOab')}
                                        disabled={false}
                                    />
                                </Box>
                                <Box sx={{ mt: ESPACAMENTO_ITEM_FORMULARIO }}>
                                    <AutoComplete
                                        value={this.sexoAtual}
                                        options={sexos}
                                        onChange={this.updateSexoAtual}
                                        label="Gênero"
                                    />
                                </Box>
                                {this.observableProps.parteAtual && (
                                    <Box sx={{ mt: ESPACAMENTO_ITEM_FORMULARIO }}>
                                        <AutoComplete
                                            dense
                                            outlined
                                            disabled
                                            id="parteRepresentadaEdicao"
                                            value={
                                                this.observableProps.parteAtual
                                                    ? this.observableProps.parteAtual.nome
                                                    : ''
                                            }
                                            options={[]}
                                            label="Parte Representada"
                                        />
                                    </Box>
                                )}
                                {this.advogado && (
                                    <Box sx={{ mt: ESPACAMENTO_ITEM_FORMULARIO }}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DateTimePicker
                                                ampm={false}
                                                label="Data de solicitação da inscrição"
                                                format="DD/MM/YYYY HH:mm:ss"
                                                views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                                                value={this.dataSolicitacaoSustentacao}
                                                onChange={this.handleChangeDataSolicitacao}
                                                disabled={true}
                                            />
                                        </LocalizationProvider>
                                    </Box>
                                )}
                            </>
                        </DialogContent>
                        <DialogActions sx={{ mr: 2, mb: 1 }}>
                            <Button
                                color="secondary"
                                variant="outlined"
                                endIcon={<CancelIcon />}
                                onClick={this.fechar}
                            >
                                Cancelar
                            </Button>
                            <Tooltip title={<Typography className="tooltipBotao">{this.dicaBotaoGravar}</Typography>}>
                                <span>
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        endIcon={this.advogado ? <SaveIcon /> : <AddCircleIcon />}
                                        sx={{ ml: 2 }}
                                        disabled={
                                            this.isDadosNovosIguaisAosAntigos ||
                                            !this.verificarItensObrigatoriosPreenchidos()
                                        }
                                        onClick={this.handleClickGravarSustentacaoOral}
                                    >
                                        {this.advogado ? 'Salvar' : 'Incluir'}
                                    </Button>
                                </span>
                            </Tooltip>
                        </DialogActions>
                    </Dialog>
                )}
            </>
        );
    }
}

export default inject('stores')(observer(ModalEdicaoAdvogadoProcesso));
